import { useState } from "react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle, Clock, XCircle, FileImage, Upload } from "lucide-react";
import { usePaymentSlips, formatRelativeTime, type PaymentSlip } from "./hooks";
import { PaymentSlipDetail } from "./PaymentSlipDetail";

interface PaymentSlipListProps {
  statusFilter?: string;
}

const STATUS_BADGE: Record<
  string,
  { variant: "default" | "secondary" | "destructive" | "outline"; icon: typeof Clock; className?: string }
> = {
  Uploaded: { variant: "outline", icon: Upload, className: "bg-blue-50 text-blue-700 border-blue-200" },
  Pending: { variant: "secondary", icon: Clock },
  Verified: { variant: "default", icon: CheckCircle },
  Rejected: { variant: "destructive", icon: XCircle },
};

export function PaymentSlipList({ statusFilter }: PaymentSlipListProps) {
  const { data: rawSlips, isLoading } = usePaymentSlips(statusFilter);
  const slips: PaymentSlip[] = Array.isArray(rawSlips) ? rawSlips : [];
  const [selectedSlip, setSelectedSlip] = useState<PaymentSlip | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-14 w-full rounded-sm" />
        ))}
      </div>
    );
  }

  if (!slips.length) {
    const emptyMessages: Record<string, string> = {
      Uploaded: "No uploaded payment slips awaiting approval.",
      Pending: "No pending payment slips to review.",
      Verified: "No verified payment slips.",
      Rejected: "No rejected payment slips.",
    };
    return (
      <div className="flex flex-col items-center gap-2 rounded border border-dashed p-8 text-center">
        <FileImage className="h-10 w-10 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">
          {statusFilter
            ? emptyMessages[statusFilter] ?? "No payment slips found."
            : "No payment slips uploaded yet."}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-hidden rounded border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                Order
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden sm:table-cell">
                Uploaded
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {slips.map((slip) => {
              const badge = STATUS_BADGE[slip.status] ?? STATUS_BADGE.Pending;
              const BadgeIcon = badge.icon;
              return (
                <tr
                  key={slip.name}
                  onClick={() => setSelectedSlip(slip)}
                  className="cursor-pointer border-b last:border-0 transition-colors hover:bg-muted/30"
                >
                  <td className="px-4 py-3 font-medium">{slip.sales_order}</td>
                  <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">
                    {slip.uploaded_at
                      ? formatRelativeTime(slip.uploaded_at)
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={badge.variant} className={cn(badge.className)}>
                      <BadgeIcon className="mr-1 h-3 w-3" />
                      {slip.status}
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedSlip && (
        <PaymentSlipDetail
          key={selectedSlip.name}
          slip={selectedSlip}
          open={!!selectedSlip}
          onOpenChange={(v) => !v && setSelectedSlip(null)}
        />
      )}
    </>
  );
}

